import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("token");

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    // If no token in URL, redirect to login
    useEffect(() => {
        if (!token) {
            toast.error("Lyen pa valid. Tanpri retounen nan paj login.");
            navigate("/linkamazon/login");
        }
    }, [token, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            toast.error("Modpas yo pa menm!");
            return;
        }
        if (newPassword.length < 6) {
            toast.error("Modpas dwe gen omwen 6 karaktè");
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${API}/linkamazon/auth/reset-password`, {
                token: token,
                new_password: newPassword
            });
            toast.success(response.data.message || "Modpas chanje avèk siksè!");
            setDone(true);
            // Auto-redirect to login after 3 seconds
            setTimeout(() => navigate("/linkamazon/login"), 3000);
        } catch (error) {
            const msg = error.response?.data?.detail || "Erè nan chanje modpas";
            toast.error(msg);
            // If token expired or invalid, offer to go back
            if (error.response?.status === 400) {
                setTimeout(() => navigate("/linkamazon/login"), 3000);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#FF9900] to-[#FFB84D] flex items-center justify-center p-4">
            <Card className="w-full max-w-md shadow-2xl border-0">

                {/* Header */}
                <div className="bg-gradient-to-r from-[#FF9900] to-[#FFB84D] p-6 text-center rounded-t-xl">
                    <div className="text-4xl mb-2">🔑</div>
                    <h1 className="text-white font-bold text-2xl">Nouvo Modpas</h1>
                    <p className="text-white/80 text-sm mt-1">BVGLINKAMAZON</p>
                </div>

                <CardContent className="p-6">
                    {!done ? (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <p className="text-gray-500 text-sm text-center">
                                Antre yon nouvo modpas pou kont ou a.
                            </p>

                            <div>
                                <Label className="text-gray-700 font-medium">Nouvo Modpas</Label>
                                <Input
                                    type="password"
                                    placeholder="••••••••"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    autoFocus
                                    data-testid="reset-new-password"
                                    className="mt-1 h-11"
                                />
                            </div>

                            <div>
                                <Label className="text-gray-700 font-medium">Konfime Nouvo Modpas</Label>
                                <Input
                                    type="password"
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    data-testid="reset-confirm-password"
                                    className="mt-1 h-11"
                                />
                                {confirmPassword && newPassword !== confirmPassword && (
                                    <p className="text-red-500 text-xs mt-1">⚠️ Modpas yo pa menm</p>
                                )}
                                {confirmPassword && newPassword === confirmPassword && confirmPassword.length >= 6 && (
                                    <p className="text-green-600 text-xs mt-1">✅ Modpas yo menm</p>
                                )}
                            </div>

                            {/* Password strength indicator */}
                            {newPassword && (
                                <div>
                                    <div className="flex gap-1 mt-1">
                                        {[...Array(4)].map((_, i) => (
                                            <div
                                                key={i}
                                                className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${newPassword.length >= (i + 1) * 2
                                                        ? newPassword.length >= 10
                                                            ? "bg-green-500"
                                                            : newPassword.length >= 6
                                                                ? "bg-yellow-500"
                                                                : "bg-red-400"
                                                        : "bg-gray-200"
                                                    }`}
                                            />
                                        ))}
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">
                                        {newPassword.length < 6 ? "Tro kout" : newPassword.length < 10 ? "Akseptab" : "Fò 💪"}
                                    </p>
                                </div>
                            )}

                            <Button
                                type="submit"
                                disabled={loading || newPassword !== confirmPassword || newPassword.length < 6}
                                className="w-full bg-[#FF9900] hover:bg-[#E88B00] text-white font-bold py-6 text-lg mt-2"
                                data-testid="reset-submit-btn"
                            >
                                {loading ? (
                                    <span className="flex items-center gap-2">
                                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                        Ap chanje...
                                    </span>
                                ) : (
                                    "🔒 Chanje Modpas"
                                )}
                            </Button>

                            <div className="text-center">
                                <button
                                    type="button"
                                    onClick={() => navigate("/linkamazon/login")}
                                    className="text-sm text-gray-500 hover:text-[#FF9900]"
                                >
                                    ← Retounen nan Login
                                </button>
                            </div>
                        </form>
                    ) : (
                        /* Success state */
                        <div className="text-center py-6 space-y-4">
                            <div className="text-6xl animate-bounce">✅</div>
                            <h2 className="text-xl font-bold text-gray-800">Modpas Chanje!</h2>
                            <p className="text-gray-500">
                                Siksè! Modpas ou chanje. Nou ap voye ou nan paj login nan 3 segonn...
                            </p>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                    className="bg-[#FF9900] h-2 rounded-full animate-[shrink_3s_linear_forwards]"
                                    style={{ animation: "shrink 3s linear forwards", width: "100%" }}
                                />
                            </div>
                            <Button
                                onClick={() => navigate("/linkamazon/login")}
                                className="bg-[#FF9900] hover:bg-[#E88B00] text-white font-bold px-8"
                            >
                                Konekte Kounye a →
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

